import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Login() {
  const [mode, setMode] = useState("login"); // login | register-owner
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [workspace, setWorkspace] = useState("");
  const [mfa, setMfa] = useState("");
  const [needsMfa, setNeedsMfa] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login, registerOwner } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password, mfa || undefined);
        toast.success("Welcome back.");
        nav("/opportunities");
      } else {
        await registerOwner({ email, password, name, workspace_name: workspace });
        toast.success("Workspace created. Welcome to WingWise.");
        nav("/opportunities");
      }
    } catch (e) {
      const reason = e?.response?.headers?.["x-auth-reason"];
      if (reason === "mfa_required") {
        setNeedsMfa(true);
        toast.error("MFA code required.");
      } else {
        toast.error(e?.response?.data?.detail || "Authentication failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left: brand */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 border-r border-border/60 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.15]" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBtb2RlJTIwZmluYW5jZSUyMHRleHR1cmV8ZW58MHx8fHwxNzg1NjAyMTMzfDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 0%, hsl(var(--background)) 100%)" }}/>
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <img src="/wingwise-mark.svg" alt="" className="w-8 h-8" aria-hidden="true" />
            <div className="text-xl font-semibold tracking-tight">Wing<span className="text-[#0094FF]">Wise</span></div>
          </div>
          <div className="mt-2 text-xs mono uppercase tracking-widest text-muted-foreground">
            Your AI Investing Wingman · Analyze · Manage Risk · Decide
          </div>
        </div>
        <div className="relative z-10 space-y-6 max-w-md">
          <div>
            <div className="label-cap mb-2">Doctrine</div>
            <div className="text-2xl font-medium leading-snug">
              AI recommends. <span className="text-muted-foreground">Risk engine decides.</span> Human approves.
            </div>
          </div>
          <div className="grid grid-cols-3 gap-px bg-border/60">
            {[
              { k: "Two-agent debate", v: "Analyst vs Critic" },
              { k: "Deterministic risk", v: "Veto power" },
              { k: "Paper only", v: "Zero live risk" },
            ].map((c) => (
              <div key={c.k} className="p-3 bg-card">
                <div className="label-cap">{c.k}</div>
                <div className="text-xs mt-1">{c.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-[10px] mono uppercase tracking-widest text-muted-foreground">
          MVP · US Equities & ETFs · Long-only
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div>
            <div className="label-cap">{mode === "login" ? "Sign in" : "Bootstrap Workspace"}</div>
            <h1 className="text-2xl font-semibold tracking-tight mt-1">
              {mode === "login" ? "Access your terminal" : "Create household workspace"}
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              {mode === "login"
                ? "Invite-only. If you're the first user, bootstrap the workspace."
                : "The first user becomes Owner and can invite Traders."}
            </p>
          </div>

          {mode === "register-owner" && (
            <>
              <div>
                <div className="label-cap mb-1">Your Name</div>
                <Input value={name} onChange={(e)=>setName(e.target.value)} required className="!rounded-none" data-testid={AUTH.nameInput}/>
              </div>
              <div>
                <div className="label-cap mb-1">Workspace Name</div>
                <Input value={workspace} onChange={(e)=>setWorkspace(e.target.value)} required className="!rounded-none" data-testid={AUTH.workspaceInput}/>
              </div>
            </>
          )}

          <div>
            <div className="label-cap mb-1">Email</div>
            <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required className="!rounded-none" data-testid={AUTH.emailInput}/>
          </div>
          <div>
            <div className="label-cap mb-1">Password</div>
            <Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required className="!rounded-none" data-testid={AUTH.passwordInput}/>
          </div>

          {needsMfa && (
            <div>
              <div className="label-cap mb-1">MFA Code</div>
              <Input value={mfa} onChange={(e)=>setMfa(e.target.value)} inputMode="numeric" className="!rounded-none mono" data-testid={AUTH.mfaCodeInput}/>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full"
            data-testid={mode === "login" ? AUTH.loginBtn : AUTH.registerBtn}
          >
            {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Create workspace"}
          </button>

          <div className="text-xs text-muted-foreground text-center">
            {mode === "login" ? (
              <>
                No workspace yet?{" "}
                <button type="button" className="text-foreground underline underline-offset-2" onClick={()=>setMode("register-owner")}>
                  Bootstrap one
                </button>
              </>
            ) : (
              <>
                Have an account?{" "}
                <button type="button" className="text-foreground underline underline-offset-2" onClick={()=>setMode("login")}>
                  Sign in
                </button>
              </>
            )}
            <div className="mt-2">
              Received an invite?{" "}
              <Link className="text-foreground underline underline-offset-2" to="/register">Accept invite</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
